<template>
  <section class="py-4 md:py-8">
    <div class="relative">
      <!-- Subtle gradient glow around the card -->
      <div class="absolute -inset-1 rounded-3xl bg-gradient-to-r from-yellow-500/20 via-blue-500/15 to-yellow-500/20 blur-xl opacity-60"></div>
      
      <!-- Main hero card -->
      <div class="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
        <div class="text-center p-8 md:p-12 lg:p-16">
          <!-- Duck Image -->
          <Motion
            :initial="{ opacity: 0, scale: 0.9 }"
            :animate="{ opacity: 1, scale: 1 }"
            :transition="{ type: 'spring', stiffness: 100, damping: 15, duration: 0.8 }"
          >
            <div class="relative inline-block mb-6">
              <img 
                src="/ducksImages/duckIcon.webp" 
                alt="Cruise duck tags with QR codes" 
                class="w-32 h-32 md:w-48 md:h-48 object-contain mx-auto drop-shadow-2xl animate-float" 
              />
              <!-- Decorative glow -->
              <div class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 md:w-48 md:h-48 bg-gradient-to-br from-yellow-400/30 to-blue-400/30 rounded-full blur-3xl -z-10"></div>
            </div>
          </Motion>

          <!-- Heading -->
          <Motion
            :initial="{ opacity: 0, y: 20 }"
            :animate="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.6, delay: 0.1 }"
          >
            <h1 class="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight text-slate-800 mb-6">
              Hide Ducks. <span class="text-yellow-500">Create Joy.</span> 
              Connect on The Seas.
            </h1>
          </Motion>
          
          <!-- Description -->
          <Motion
            :initial="{ opacity: 0, y: 20 }"
            :animate="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.6, delay: 0.2 }"
          >
            <p class="text-base md:text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto mb-8">
              Get custom <strong class="text-yellow-600">cruise duck tags</strong> with unique <strong class="text-yellow-600">QR codes</strong>. 
              Hide them on your <strong class="text-yellow-600">cruise ship</strong>, and when passengers find them, they <strong class="text-yellow-600">scan and message</strong> you! 
              Track discoveries in <strong class="text-yellow-600">real-time</strong> and compete on <strong class="text-yellow-600">leaderboards</strong>.
            </p>
          </Motion>
          
          <!-- CTA Buttons -->
          <Motion
            :initial="{ opacity: 0, y: 20 }"
            :animate="{ opacity: 1, y: 0 }"
            :transition="{ duration: 0.6, delay: 0.3 }"
          >
            <div class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              <button
                @click="$emit('scroll-to', 'pricing')"
                class="inline-flex items-center px-8 md:px-10 py-3.5 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-bold transition-all transform hover:scale-105 shadow-lg text-base md:text-lg"
              >
                Get Started
                <v-icon class="ml-2" size="small">mdi-chevron-down</v-icon>
              </button>
              
              <router-link 
                to="/story"
                class="inline-flex items-center px-6 md:px-8 py-3 bg-white hover:bg-gray-50 border-2 border-yellow-400 text-gray-800 rounded-xl font-semibold transition-all transform hover:scale-105 hover:border-yellow-500 shadow-md text-base"
              >
                <v-icon size="20" class="mr-2" color="yellow-darken-2">mdi-book-open-variant</v-icon>
                The Duck Tradition
                <v-icon size="20" class="ml-2" color="yellow-darken-2">mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </Motion>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Motion } from 'motion-v';

defineEmits<{
  (e: 'scroll-to', sectionId: string): void;
}>();
</script>

<style scoped>
/* Floating animations matching the rest of the site */
.animate-float {
  animation: float 3s ease-in-out infinite;
  will-change: transform;
}

.animate-float-reverse {
  animation: float-reverse 2.5s ease-in-out infinite;
  will-change: transform;
}

.animate-float-subtle {
  animation: float-subtle 4s ease-in-out infinite;
  will-change: transform;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(5deg);
  }
}

@keyframes float-reverse {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(-3deg);
  }
}

@keyframes float-subtle {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.05);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .animate-float,
  .animate-float-reverse,
  .animate-float-subtle {
    animation-duration: 4s;
  }
}
</style>
